import { type ReactNode, type CSSProperties } from 'react'
import cl from 'clsx'

type TableColumnDescription<Key extends string> = {
    key: Key
    width: number
}

type Padding = [className: string, extraWidth: number]

const TABLE_COLUMN_PADDING: Padding = ['px-2', 8 + 8]
const TABLE_COLUMN_PADDING_FIRST: Padding = ['pl-1 pr-2', 4 + 8]
const TABLE_COLUMN_PADDING_LAST: Padding = ['pl-2 pr-1', 8 + 4]

const makeTableColumnsLayout = <Key extends string>(
    tableColumnsOrder: readonly TableColumnDescription<Key>[]
) => {
    const processItem = (
        input: TableColumnDescription<Key>,
        className: string,
        extraWidth: number
    ) => {
        const width = input.width + extraWidth

        return {
            key: input.key,
            className: cl(className),
            style: {
                width,
                minWidth: width,
                maxWidth: width,
            },
        }
    }

    const first = tableColumnsOrder[0]
    const last = tableColumnsOrder[tableColumnsOrder.length - 1]

    const result: {
        key: Key
        style: CSSProperties
        className: string
    }[] = [
        processItem(first, ...TABLE_COLUMN_PADDING_FIRST),
        ...tableColumnsOrder
            .slice(1, -1)
            .map((column) => processItem(column, ...TABLE_COLUMN_PADDING)),
        processItem(last, ...TABLE_COLUMN_PADDING_LAST),
    ]

    return result
}

const TABLE_COLUMNS_SPEC = [
    //from figma
    { key: 'typeText', width: 92 },
    { key: 'lastUpdate', width: 71 },
    { key: 'status', width: 58 },
    { key: 'description', width: 93 },
    { key: 'id', width: 71 },
] as const

export const TABLE_COLUMNS_LAYOUT = makeTableColumnsLayout(TABLE_COLUMNS_SPEC)

export type TableColumnKey = (typeof TABLE_COLUMNS_SPEC)[number]['key']

export type TableReactNodeRecord = Record<TableColumnKey, ReactNode>
